export default class BlockWave extends HTMLElement {

  constructor() {
    super();

    this.attachShadow({ mode: "open" });

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
          height: 100%;
          overflow: hidden;
        }

        svg {
          width: 100%;
          height: 100%;
          display: block;
        }

        path {
          fill: none;
          stroke-linecap: round;
          stroke-linejoin: round;
          vector-effect: non-scaling-stroke;
        }

        .line-a {
          stroke: #cf9c8e;
        }

        .line-b {
          stroke: #426e66;
        }
      </style>

      <svg preserveAspectRatio="none">
        <path class="line-a"></path>
        <path class="line-b"></path>
      </svg>
    `;

    this.svg = this.shadowRoot.querySelector("svg");
    this.pathA = this.shadowRoot.querySelector(".line-a");
    this.pathB = this.shadowRoot.querySelector(".line-b");

    this.width = 1;
    this.height = 1;
    this.time = 0;
    this.raf = null;
    this.scrollProgress = 0;
    this.smoothedScrollProgress = 0;

    this.resizeObserver = new ResizeObserver(() => this.updateSize());

    this.handleScroll = () => this.updateScrollProgress();

    // Pause the RAF when the wave is off-screen — there is no point drawing
    // 500-point paths twice per frame while the user is looking elsewhere.
    this._inView = false;
    this._io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const wasInView = this._inView;
        this._inView = e.isIntersecting;
        if (this._inView && !wasInView && !this.raf) {
          this.animate();
        }
      });
    }, { rootMargin: '100px' });


    this.siteSettingsMotionChange = this.siteSettingsMotionChange.bind(this);
    this.stopped = false;

  }

  getResponsiveStrokeWidth() {
    const refWidth = 1728;
    const minStroke = 1.5;
    const maxStroke = 3;
    return this.clamp(
      (window.innerWidth / refWidth) * maxStroke,
      minStroke,
      maxStroke
    );
  }

  connectedCallback() {
    this.updateSize();
    this.updateScrollProgress();

    this.resizeObserver.observe(this);
    this._io.observe(this);

    window.addEventListener("scroll", this.handleScroll, { passive: true });
    window.addEventListener("resize", this.handleScroll);

    this.animate();

    document.addEventListener('site_settings_motion', this.siteSettingsMotionChange, false);

  }

  siteSettingsMotionChange(event) {
    this.stopped = ('off' === event.detail);
  }

  disconnectedCallback() {
    this.resizeObserver.disconnect();
    this._io?.disconnect();

    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("resize", this.handleScroll);

    cancelAnimationFrame(this.raf);
  }

  updateSize() {
    this.width = this.clientWidth || 1;
    this.height = this.clientHeight || 1;
    const strokeWidth = this.getResponsiveStrokeWidth();
    this.pathA.style.strokeWidth = strokeWidth;
    this.pathB.style.strokeWidth = strokeWidth;
    this.svg.setAttribute("viewBox", `0 0 ${this.width} ${this.height}`);
    this.updateScrollProgress();
  }

  clamp(value, min = 0, max = 1) {
    return Math.min(max, Math.max(min, value));
  }

  lerp(a, b, t) {
    return a + (b - a) * t;
  }

  easeInOut(t) {
    return t * t * (3 - 2 * t);
  }

  updateScrollProgress() {
    const rect = this.getBoundingClientRect();
    const viewportHeight = window.innerHeight || 1;

    const elementCenter = rect.top + rect.height / 2;

    // 0 = component center is at the bottom of the screen
    // 1 = component center is at the middle of the screen
    const start = viewportHeight;
    const end = viewportHeight * 0.5;

    const rawProgress = (start - elementCenter) / (start - end);

    this.scrollProgress = this.easeInOut(this.clamp(rawProgress));
  }

  createSmoothPath(points) {
    if (!points.length) return "";

    // Build path with array.push + join — avoids the ~500 incremental string
    // allocations / GC pressure that += produced in a hot RAF loop.
    const parts = new Array(points.length + 1);
    parts[0] = `M${points[0][0].toFixed(1)} ${points[0][1].toFixed(1)}`;

    const last = points.length - 1;
    for (let i = 1; i < last; i++) {
      const p  = points[i];
      const p2 = points[i + 1];
      const mx = (p[0] + p2[0]) * 0.5;
      const my = (p[1] + p2[1]) * 0.5;
      parts[i] = `Q${p[0].toFixed(1)} ${p[1].toFixed(1)} ${mx.toFixed(1)} ${my.toFixed(1)}`;
    }

    const lp = points[last];
    parts[last] = `L${lp[0].toFixed(1)} ${lp[1].toFixed(1)}`;

    return parts.join(' ');
  }

  createRegularWavePath({
    width,
    height,
    time,
    baselineRatio,
    amplitudeRatio,
    amplitudeModRatio = 0,
    cycles,
    modCycles = 0,
    speed,
    phase = 0
  }) {
    const points = [];
    // Coarser sampling: ~150 points across the wave. The Bezier smoothing
    // in createSmoothPath fills the gaps invisibly — at 4× fewer points
    // the visual result is identical, the per-frame cost is roughly 1/4.
    const step = Math.max(4, width / 150);

    const baseline = height * baselineRatio;
    const baseAmplitude = height * amplitudeRatio;
    const modAmplitude = height * amplitudeModRatio;

    for (let x = -step; x <= width + step; x += step) {
      const tx = x + time * speed;

      const mainAngle = (tx / width) * cycles * Math.PI * 2 + phase;

      let amplitude = baseAmplitude;

      if (modCycles > 0 && modAmplitude > 0) {
        const modAngle = (tx / width) * modCycles * Math.PI * 2;
        amplitude += Math.sin(modAngle) * modAmplitude;
      }

      const y = baseline + Math.sin(mainAngle) * amplitude;

      points.push([x, y]);
    }

    return this.createSmoothPath(points);
  }

  mixWaveParams(from, to, progress) {
    return {
      baselineRatio: this.lerp(from.baselineRatio, to.baselineRatio, progress),
      amplitudeRatio: this.lerp(from.amplitudeRatio, to.amplitudeRatio, progress),
      amplitudeModRatio: this.lerp(from.amplitudeModRatio, to.amplitudeModRatio, progress),
      cycles: this.lerp(from.cycles, to.cycles, progress),
      modCycles: this.lerp(from.modCycles, to.modCycles, progress),
      speed: this.lerp(from.speed, to.speed, progress),
      phase: this.lerp(from.phase, to.phase, progress)
    };
  }

  animate() {

    const w = this.width;
    const h = this.height;

    this.smoothedScrollProgress += (this.scrollProgress - this.smoothedScrollProgress) * 0.008;
    
    if (this.stopped) {
      this.smoothedScrollProgress = 1;
    }
    else {
      this.time += 0.016;
    }
    
    const progress = this.smoothedScrollProgress;


    const topWave = {
      baselineRatio: 0.36,
      amplitudeRatio: 0.18,
      amplitudeModRatio: 0.07,
      cycles: 15.5,
      modCycles: 2.2,
      speed: 65,
      phase: 0.35
    };

    const bottomWave = {
      baselineRatio: 0.48,
      amplitudeRatio: 0.13,
      amplitudeModRatio: 0.045,
      cycles: 11.5,
      modCycles: 1.8,
      speed: 45,
      phase: -0.05
    };

    const pathA = this.createRegularWavePath({
      width: w,
      height: h,
      time: this.time,
      ...topWave
    });

    const mixedBottomWave = this.mixWaveParams(
      bottomWave,
      topWave,
      progress
    );

    const pathB = this.createRegularWavePath({
      width: w,
      height: h,
      time: this.time,
      ...mixedBottomWave
    });

    if (pathA !== this._lastPathA) {
      this.pathA.setAttribute("d", pathA);
      this._lastPathA = pathA;
    }
    if (pathB !== this._lastPathB) {
      this.pathB.setAttribute("d", pathB);
      this._lastPathB = pathB;
    }

    // Don't schedule another frame when off-screen — IntersectionObserver
    // will restart the loop once the wave scrolls back into view.
    if (!this._inView) {
      this.raf = null;
      return;
    }
    this.raf = requestAnimationFrame(() => this.animate());
  }
}

customElements.define("block-wave", BlockWave);